import { useAtom } from "jotai";
import {
  CompanySettingDataAtom,
  CompanySettingType,
  employeeInfoAtom,
  EmployeeInfoType,
  splashImageInfoAtom,
  loginImageInfoAtom,
  minimumUtterancesFromApiAtom,
  phrasesFromApiAtom,
  RawPhraseInfo,
  LoginImageInfoType,
  LanguageLabelsType,
  LanguageLabelsAtom,
  SplashImageInfoType,
  splashImageInfoInitial,
  accessTokenInfoAtom,
  recordingSettingAtom,
} from "../store";
import { useCallback, useRef } from "react";
import { voislogApi } from "./apiClients/voislogApi";
import { useModifiedTranslation } from "../hooks/useModifiedTranslation";
import { useToastMessage } from "../hooks/useToastMessage";
import { ApiError } from "./apiClients/ApiError";
import { sentryLog } from "../../sentry";
import { AccessToken } from "../types";

type SettingInfo = (token: AccessToken) => void;

/*
 * vManagement API からアプリ設定を取得して各 atom に保持する共通処理
 */
export function useSetSetting(): SettingInfo {
  const lastEmployeeId = useRef<string | null>(null);
  const [, setEmployeeInfo] = useAtom(employeeInfoAtom);
  const [, setSplashImageInfo] = useAtom(splashImageInfoAtom);
  const [, setLoginImageInfo] = useAtom(loginImageInfoAtom);
  const [, setLanguageLabels] = useAtom(LanguageLabelsAtom);
  const [, setPhrasesFromApi] = useAtom(phrasesFromApiAtom);
  const [, setMinimumUtterancesFromApi] = useAtom(minimumUtterancesFromApiAtom);
  const [, setCompanySettingData] = useAtom(CompanySettingDataAtom);
  const [, setRecordingSetting] = useAtom(recordingSettingAtom);

  const t = useModifiedTranslation();
  const errorToast = useToastMessage();

  return useCallback(
    async (token) => {
      // 同じユーザーの場合は何もしない
      if (lastEmployeeId.current === token.user_id) {
        return;
      }

      // Splash画像パスの初期化
      setSplashImageInfo(splashImageInfoInitial);

      // vManagement API から各設定を並列取得
      try {
        const [
          bannerRes,
          splashRes,
          recTypeRes,
          appLabelRes,
          phrasePickRes,
          recordingRes,
        ] = await Promise.all([
          voislogApi.GET("/v2/v_management/app_settings/image/{image_type}", {
            params: {
              path: { image_type: "banner" },
              query: { user_id: token.user_id },
            },
          }),
          voislogApi.GET("/v2/v_management/app_settings/image/{image_type}", {
            params: {
              path: { image_type: "splash" },
              query: { user_id: token.user_id },
            },
          }),
          voislogApi.GET("/v2/v_management/app_settings/rec_type", {
            params: {
              query: { company_id: token.company_id },
            },
          }),
          voislogApi.GET("/v2/v_management/settings/app_label", {
            params: {
              query: { company_id: token.company_id },
            },
          }),
          voislogApi.GET("/v2/v_management/app_settings/phrase/pick", {
            params: {
              query: { user_id: token.user_id },
            },
          }),
          voislogApi.GET("/v2/v_management/app_settings/recording", {
            params: {
              query: { company_id: token.company_id },
            },
          }),
        ]);

        // 1つでも取得できなかった場合は設定を反映しない
        if (bannerRes.error)
          throw new ApiError(bannerRes.response, "get", bannerRes.error);
        if (splashRes.error)
          throw new ApiError(splashRes.response, "get", splashRes.error);
        if (recTypeRes.error)
          throw new ApiError(recTypeRes.response, "get", recTypeRes.error);
        if (appLabelRes.error)
          throw new ApiError(appLabelRes.response, "get", appLabelRes.error);
        if (phrasePickRes.error)
          throw new ApiError(
            phrasePickRes.response,
            "get",
            phrasePickRes.error,
          );
        if (recordingRes.error)
          throw new ApiError(recordingRes.response, "get", recordingRes.error);

        const bannerData = bannerRes.data!;
        const splashData = splashRes.data!;
        const recTypeData = recTypeRes.data!;
        const appLabelData = appLabelRes.data!;
        const phrasePickData = phrasePickRes.data!;
        const recordingData = recordingRes.data!;

        // ユーザー情報を保存
        // employee情報取得（Core API /users/me）
        const {
          data: me,
          error: meError,
          response: meResponse,
        } = await voislogApi.GET("/v2/core/users/{user_id_or_me}", {
          params: { path: { user_id_or_me: token.user_id } },
        });
        if (meError) throw new ApiError(meResponse, "get", meError);
        const employeeInfo: EmployeeInfoType = {
          employee_id: me.email,
          employee_name: `${me.last_name} ${me.first_name}`,
          company_id: me.companies?.find((c) => c.is_member)?.id ?? "",
        };
        setEmployeeInfo(employeeInfo);
        lastEmployeeId.current = token.user_id;

        // banner: ロゴ画像（ログイン画面用）
        const loginImageInfo: LoginImageInfoType = {
          login_path: bannerData.setting_data?.image_url ?? "",
          login_no: bannerData.setting_data?.no ?? null,
        };
        setLoginImageInfo(JSON.stringify(loginImageInfo));

        // splash: スプラッシュ画像
        const splashImageInfo: SplashImageInfoType = {
          splash_path: splashData.setting_data?.image_url ?? "",
          splash_no: splashData.setting_data?.no ?? null,
        };
        setSplashImageInfo(splashImageInfo);

        // phrase: フレーズ設定
        const phrases = phrasePickData.phrases;
        const minimum_utterances = phrasePickData.minimum_utterances;
        setPhrasesFromApi(
          phrases.map<RawPhraseInfo>((p, index) => ({
            titleKey: `${t("PJ.FixedPhrase.Title")} ${index + 1}/${
              phrases.length
            }`,
            phraseKey: p,
            helperTextKey: t("PJ.FixedPhrase.HelperText"),
          })),
        );
        setMinimumUtterancesFromApi(minimum_utterances);

        // rec_type: 録音種別設定
        const rec_types = recTypeData.setting_data?.rec_types ?? undefined;
        const companySetting: CompanySettingType = {
          minimum_utterances,
          rec_types,
        };
        setCompanySettingData(companySetting);

        // app_label: 表記設定
        setLanguageLabels(appLabelData.setting_data as LanguageLabelsType);

        // recording: 録音設定
        setRecordingSetting(recordingData);
      } catch (exc) {
        sentryLog(exc);
        errorToast(exc);
      }
    },
    [
      t,
      errorToast,
      setEmployeeInfo,
      setSplashImageInfo,
      setLoginImageInfo,
      setLanguageLabels,
      setPhrasesFromApi,
      setMinimumUtterancesFromApi,
      setCompanySettingData,
      setRecordingSetting,
    ],
  );
}

/**
 * /v2/v_management/app_settings/phrase/pick を再取得して phrasesFromApiAtom を更新する。
 * ホーム画面に戻るたびに呼ぶことで、バックエンドのルールに従ったフレーズ順を再取得する。
 */
export function useRefreshPhrases(): () => Promise<void> {
  const [tokenInfo] = useAtom(accessTokenInfoAtom);
  const [, setPhrasesFromApi] = useAtom(phrasesFromApiAtom);
  const t = useModifiedTranslation();
  const errorToast = useToastMessage();

  return useCallback(async () => {
    if (!tokenInfo) return;
    try {
      const { data, error, response } = await voislogApi.GET(
        "/v2/v_management/app_settings/phrase/pick",
        {
          params: {
            query: { user_id: tokenInfo.user_id },
          },
        },
      );
      if (error) throw new ApiError(response, "get", error);
      const phrases = data!.phrases;
      setPhrasesFromApi(
        phrases.map<RawPhraseInfo>((p, index) => ({
          titleKey: `${t("PJ.FixedPhrase.Title")} ${index + 1}/${
            phrases.length
          }`,
          phraseKey: p,
          helperTextKey: t("PJ.FixedPhrase.HelperText"),
        })),
      );
    } catch (exc) {
      sentryLog(exc);
      errorToast(exc);
    }
  }, [tokenInfo, setPhrasesFromApi, t, errorToast]);
}
